import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Dashboard,
  DriveEta,
  FlightTakeoff,
  Logout,
  Sensors,
  Timeline,
} from "@mui/icons-material";
import { Avatar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as AerovectLogoWhite } from "../../assets/aerovect-logo-white.svg";
import { useFleetAuth } from "../../auth/fleetAuth";
import { usePermissions } from "../../contexts/PermissionsContext";
import {
  DEV_UNLOCK_TRACTOR_STATE,
  TRACTOR_METRICS_ENABLED,
  TRACTOR_STATE_ENABLED,
} from "../../utils/devFlags";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useFleetAuth();
  const { hasPermission } = usePermissions();
  const [isMediumScreen, setIsMediumScreen] = useState(
    window.innerWidth <= 1032,
  );
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMediumScreen(window.innerWidth <= 1032);
      setIsSmallScreen(window.innerWidth <= 600);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <NavbarContainer>
      <Header>
        {isSmallScreen ? (
          <AerovectLogoSmallA
            src={`${process.env.PUBLIC_URL}/aerovect-logo-small-a.svg`}
            alt="Aerovect Logo"
          />
        ) : (
          <AerovectLogoWhite />
        )}{" "}
      </Header>
      <Links>
        <Link
          isActive={
            location.pathname.includes("/dashboard") ||
            location.pathname === "/"
          }
          onClick={() => navigate("/dashboard")}
        >
          <Dashboard fontSize="large" />
          {!isMediumScreen && "Dashboard"}
        </Link>
        <Link
          isActive={location.pathname.includes("/vehicles")}
          onClick={() => navigate("/vehicles")}
        >
          <DriveEta fontSize="large" />
          {!isMediumScreen && "Vehicles"}
        </Link>
        {hasPermission("read:aerovect-vehicles") && (
          <Link
            isActive={
              location.pathname.includes("/flights") ||
              location.pathname.includes("/nexus")
            }
            onClick={() => navigate("/flights")}
          >
            <FlightTakeoff fontSize="large" />
            {!isMediumScreen && "Flights"}
          </Link>
        )}
        {TRACTOR_STATE_ENABLED &&
          (hasPermission("read:aerovect-vehicles") ||
            DEV_UNLOCK_TRACTOR_STATE) && (
            <Link
              isActive={location.pathname.includes("/tractor-state")}
              onClick={() => navigate("/tractor-state")}
            >
              <Sensors fontSize="large" />
              {!isMediumScreen && "Tractor State"}
            </Link>
          )}
        {TRACTOR_METRICS_ENABLED && hasPermission("read:aerovect-vehicles") && (
          <Link
            isActive={location.pathname.includes("/metrics")}
            onClick={() => navigate("/metrics")}
          >
            <Timeline fontSize="large" />
            {!isMediumScreen && "Tractor Metrics"}
          </Link>
        )}
      </Links>
      <UserSection>
        <LogoutButton onClick={() => logout()}>
          <Logout fontSize="large" />
          {!isMediumScreen && "Log out"}
        </LogoutButton>
        <ProfileButton>
          <ProfileIcon
            src={user?.picture}
            alt=""
            slotProps={{ img: { referrerPolicy: "no-referrer" } }}
          >
            {user?.email?.charAt(0).toUpperCase()}
          </ProfileIcon>
          {!isMediumScreen && <EmailSpan>{user?.email}</EmailSpan>}
        </ProfileButton>
      </UserSection>
    </NavbarContainer>
  );
};

const NavbarContainer = styled.div`
  width: 300px;
  height: 100vh;
  position: sticky;
  top: 0;
  background-color: ${(props) => props.theme.colors.navyBlue};
  padding: 40px;
  color: ${(props) => props.theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 5001;

  /*
   * Stacked above the content instead of beside it.
   *
   * The vertical padding used to be 40px against a declared height of 50px.
   * With the app's global border-box sizing, 80px of padding in a 50px box
   * leaves a content area of zero: the logo and the links overflowed the navy
   * bar, and because this container is sticky at z-index 5001 they overlapped
   * whatever was beneath. That is the misalignment on a narrow window — the bar
   * looked blank because its contents were outside it.
   *
   * Height is now the content's, with a floor, and the padding fits inside it.
   */
  @media (max-width: 1032px) {
    flex-direction: row;
    align-items: center;
    height: auto;
    min-height: 56px;
    width: 100%;
    padding: 8px 16px;
    gap: 10px;
  }
`;

const AerovectLogoSmallA = styled.img`
  height: 100%;
  width: 40px;
  margin-top: 12px;

  /* No stretching to a row's height, and no nudge meant for a column. */
  @media (max-width: 1032px) {
    height: 32px;
    width: auto;
    margin-top: 0;
    flex: none;
  }
`;

const Header = styled.h1`
  margin: 0;
  padding: 25px 0;
  font-size: 36px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};

  /*
   * In a row, this is a logo slot rather than a page heading: 50px of vertical
   * padding sized for a sidebar was the other half of the too-tall bar, and the
   * wordmark rendered at its intrinsic size on top of it. Both are capped here
   * so the bar stays the height the container asks for.
   */
  @media (max-width: 1032px) {
    padding: 0;
    display: flex;
    align-items: center;
    flex: none;

    svg {
      height: 26px;
      width: auto;
    }
  }
`;

const Links = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 1032px) {
    flex-direction: row;
    justify-content: center;
    gap: 10px;
  }

  /* Five items no longer fit beside the logo and the user section on a phone
     (SW-8773 added Tractor Metrics). The row scrolls inside the bar instead of
     pushing the user section off the page and the whole page sideways. */
  @media (max-width: 600px) {
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-start;
    overflow-x: auto;
    scrollbar-width: none;
  }
`;

const Link = styled.li<{ isActive: boolean }>`
  font-size: 24px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 20px;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  background-color: ${(props) =>
    props.isActive ? "rgba(255, 255, 255, 0.1)" : "none"};
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 1032px) {
    width: min-content;
    flex: 0 0 auto;
    padding: 5px 10px;
  }
`;

const UserSection = styled.div`
  margin-top: auto;
  width: 100%;
  border-top: 2px solid white;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 1032px) {
    border: none;
    padding-top: 0;
    flex-direction: row;
    margin-top: 0;
    width: min-content;
  }
`;

const UserButton = styled.button`
  font-size: 16px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 20px;
  padding: 10px;
  border-radius: 5px;
  align-items: center;
  cursor: pointer;
  background-color: transparent;
  color: ${(props) => props.theme.colors.white};
  border: none;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 1032px) {
    padding: 5px 10px;
  }
`;

const LogoutButton = styled(UserButton)`
  font-size: 20px;
`;

const ProfileButton = styled(UserButton)`
  font-size: 12px;
  gap: 20px;
  cursor: auto;

  &:hover {
    background-color: transparent;
  }
`;

/*
 * The Cognito pools map no `picture` attribute, so most sessions have none.
 * A bare <img> without a src drew the browser's broken-image outline here.
 * Avatar shows the email's first letter when the picture is missing or fails
 * to load, and crops a picture that is not square instead of stretching it.
 */
const ProfileIcon = styled(Avatar)`
  && {
    width: 30px;
    height: 30px;
    flex: none;
    /* Avatar's default Roboto sits a capital 1.5px above center; Inter does not. */
    font-family: inherit;
    font-size: 16px;
    font-weight: ${(props) => props.theme.fontWeight.semibold};
    color: ${(props) => props.theme.colors.white};
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const EmailSpan = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 150px; // Adjust the max-width as needed
`;

export default Navbar;
